'use client';

import { getCustomerGuestCpf } from '@/actions';
import { useForm, zodResolver, type Control, type FieldErrors } from '@/libs';
import { z } from '@/libs/zod';
import { useHumanPass } from '@/providers/HumanPassProvider';
import { isHumanPassDenied, parseDigits, validateCPF } from '@/utils';

import { RECOVERY_MESSAGES } from '../constants';

const recoveryCpfSchema = z
  .object({
    document: z
      .string()
      .trim()
      .min(1, 'CPF é obrigatório')
      .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF deve ter o formato 000.000.000-00'),
  })
  .superRefine((data, ctx) => {
    const cpfError = validateCPF(parseDigits(data.document));
    if (cpfError) {
      ctx.addIssue({
        path: ['document'],
        code: z.ZodIssueCode.custom,
        message: cpfError,
      });
    }
  });

type RecoveryCpfFormData = z.infer<typeof recoveryCpfSchema>;

export type RecoveryCustomerData = {
  customerId: string;
  cpf: string;
};

type UseRecoveryCpfFormProps = {
  onSubmitSuccess: (data: RecoveryCustomerData) => void;
};

type UseRecoveryCpfFormReturn = {
  control: Control<RecoveryCpfFormData>;
  errors: FieldErrors<RecoveryCpfFormData>;
  isLoading: boolean;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

const setNotFoundError = (
  setError: (name: 'document', error: { type: string; message: string }) => void,
) => {
  setError('document', {
    type: 'manual',
    message: RECOVERY_MESSAGES.CPF_NOT_FOUND,
  });
};

export const useRecoveryCpfForm = ({
  onSubmitSuccess,
}: UseRecoveryCpfFormProps): UseRecoveryCpfFormReturn => {
  // O widget vive no HumanPassProvider, global. Daqui sai só a execução das
  // consultas protegidas: esperar o passe e repetir após re-desafio é
  // responsabilidade do provider, não de cada formulário.
  const { runWithPass } = useHumanPass();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RecoveryCpfFormData>({
    resolver: zodResolver(recoveryCpfSchema),
    defaultValues: {
      document: '',
    },
  });

  const onSubmit = async (data: RecoveryCpfFormData) => {
    const customer = await runWithPass(() =>
      getCustomerGuestCpf(parseDigits(data.document)),
    );

    if (isHumanPassDenied(customer)) {
      // Nem a repetição após o re-desafio passou. Mostra o motivo em vez de
      // dizer que o CPF não existe, o que mandaria o usuário para o caminho
      // errado.
      setError('document', { message: customer.error });
      return;
    }

    if (!customer?.customerId) {
      setNotFoundError(setError);
      return;
    }

    onSubmitSuccess({
      customerId: customer.customerId,
      cpf: parseDigits(data.document),
    });
  };

  return {
    control,
    errors,
    isLoading: isSubmitting,
    handleSubmit: handleSubmit(onSubmit),
  };
};
